import { useMemo, useState } from 'react'
import {
    useFieldArray,
    useFormContext,
    useWatch,
} from 'react-hook-form'
import { Plus, Trash2 } from 'lucide-react'

import type { LotOption } from '@/components/lots/lot-items-field'
import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { Input } from '@/components/ui/input'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/**
 * Nhãn hiển thị cho bảng con nguồn — mỗi chặng (phân loại / lấy hương / thành
 * phẩm) truyền bộ nhãn riêng (prefix sl_ / scl_ / pll_).
 */
export interface LotSourceLabels {
    title: string
    empty: string
    add: string
    remove: string
    lotPlaceholder: string
    qtyLabel: string
}

interface LotSourcesSubTableProps {
    idx: number
    options: LotOption[]
    onSearchChange: (s: string) => void
    clientFilter?: boolean
    labels: LotSourceLabels
    /** Tên field mảng dòng item, mặc định 'items'. */
    itemsFieldName?: string
}

interface SourceSibling {
    source_lot_id?: string
}

/** Một dòng lô nguồn: picker lô + ô kg xuất + nút "lấy hết" + nút xoá. */
function SourceRow({
    itemsFieldName,
    idx,
    sIdx,
    options,
    onSearchChange,
    clientFilter,
    labels,
    onRemove,
}: {
    itemsFieldName: string
    idx: number
    sIdx: number
    options: LotOption[]
    onSearchChange: (s: string) => void
    clientFilter?: boolean
    labels: LotSourceLabels
    onRemove: () => void
}) {
    const base = `${itemsFieldName}.${idx}.sources.${sIdx}`
    const { control, register, setValue, formState } = useFormContext()
    const picked = useWatch({ control, name: `${base}.source_lot_id` }) as
        | string
        | undefined
    // Lô đã chọn ở các dòng nguồn KHÁC của cùng item → loại khỏi options để không
    // chọn trùng (bảng con có UNIQUE(item, lô); trùng sẽ vỡ khi lưu).
    const siblings = useWatch({
        control,
        name: `${itemsFieldName}.${idx}.sources`,
    }) as SourceSibling[] | undefined
    const rowOptions = useMemo(
        () =>
            options.filter((o) => {
                if (o.value === picked) return true
                return !(siblings ?? []).some(
                    (s, i) => i !== sIdx && s?.source_lot_id === o.value,
                )
            }),
        [options, siblings, sIdx, picked],
    )
    const errRoot = (
        formState.errors as unknown as Record<
            string,
            Array<{
                sources?: Array<Record<string, { message?: string }>>
            }>
        >
    )[itemsFieldName]?.[idx]?.sources?.[sIdx]
    // Lô đã chọn được cache để nhãn + hint "còn X" không rớt khỏi trigger khi
    // search server đổi options (server chỉ trả 50 dòng theo từ khoá hiện tại).
    // Xem PR #525 / mem:gotchas combobox server-search.
    const [pickedCache, setPickedCache] = useState<Map<string, LotOption>>(
        new Map(),
    )
    const pickedOption =
        options.find((o) => o.value === picked) ??
        (picked ? pickedCache.get(picked) : undefined)
    const remaining = pickedOption?.available

    return (
        <div className="mt-1 grid gap-2 sm:grid-cols-2">
            <div className="min-w-0">
                <Combobox
                    options={rowOptions}
                    value={picked ?? ''}
                    onChange={(v) => {
                        const opt = options.find((o) => o.value === v)
                        if (opt)
                            setPickedCache((prev) => {
                                if (prev.has(v)) return prev
                                const next = new Map(prev)
                                next.set(v, opt)
                                return next
                            })
                        setValue(`${base}.source_lot_id`, v, {
                            shouldValidate: true,
                        })
                    }}
                    onSearchChange={clientFilter ? undefined : onSearchChange}
                    shouldFilter={!!clientFilter}
                    selectedOption={pickedOption}
                    onOpenChange={
                        clientFilter
                            ? undefined
                            : (open) => {
                                  if (!open) onSearchChange('')
                              }
                    }
                    placeholder={labels.lotPlaceholder}
                />
                {errRoot?.source_lot_id?.message && (
                    <p className="mt-1 text-xs text-destructive">
                        {errRoot.source_lot_id.message}
                    </p>
                )}
                {remaining != null && (
                    <p className="mt-1 text-xs text-muted-foreground">
                        {m.lot_items_available_hint({
                            qty: formatQty(remaining),
                            unit: 'kg',
                        })}
                    </p>
                )}
            </div>
            <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <Input
                            type="number"
                            step="0.001"
                            min="0"
                            placeholder={labels.qtyLabel}
                            {...register(`${base}.source_quantity`, {
                                setValueAs: (v) =>
                                    v === '' || v == null
                                        ? undefined
                                        : Number(v),
                            })}
                        />
                        {remaining != null && remaining > 0 && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="shrink-0"
                                onClick={() =>
                                    setValue(
                                        `${base}.source_quantity`,
                                        remaining,
                                        { shouldValidate: true },
                                    )
                                }
                            >
                                {m.lot_items_take_all()}
                            </Button>
                        )}
                    </div>
                    {errRoot?.source_quantity?.message && (
                        <p className="mt-1 text-xs text-destructive">
                            {errRoot.source_quantity.message}
                        </p>
                    )}
                </div>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    aria-label={labels.remove}
                    onClick={onRemove}
                >
                    <Trash2 className="size-4 text-destructive" />
                </Button>
            </div>
        </div>
    )
}

/**
 * Bảng con NHIỀU lô nguồn của MỘT dòng phiếu nhập (chỉ hiện với lý do đi chuỗi).
 * Dùng chung cho cả 3 chặng — thay cho picker lô nguồn đơn trước đây.
 */
export function LotSourcesSubTable({
    idx,
    options,
    onSearchChange,
    clientFilter,
    labels,
    itemsFieldName = 'items',
}: LotSourcesSubTableProps) {
    const { control, formState } = useFormContext()
    const { fields, append, remove } = useFieldArray({
        control,
        name: `${itemsFieldName}.${idx}.sources`,
    })
    // Lỗi cấp-mảng (superRefine addIssue ở path [items, idx, 'sources']) khi CHƯA có
    // dòng nguồn nào — RHF lưu ở .message hoặc .root.message tuỳ phiên bản resolver.
    const arrErr = (
        formState.errors as unknown as Record<
            string,
            Array<{
                sources?: { message?: string; root?: { message?: string } }
            }>
        >
    )[itemsFieldName]?.[idx]?.sources
    const arrErrMsg =
        fields.length === 0
            ? (arrErr?.message ?? arrErr?.root?.message)
            : undefined

    return (
        <div className="mt-2 rounded-md border border-dashed bg-muted/30 p-2">
            <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                    {labels.title}
                </span>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                        append({ source_lot_id: '', source_quantity: undefined })
                    }
                >
                    <Plus className="size-4 mr-1" />
                    {labels.add}
                </Button>
            </div>
            {fields.length === 0 ? (
                <p
                    className={
                        arrErrMsg
                            ? 'text-xs text-destructive'
                            : 'text-xs text-muted-foreground'
                    }
                >
                    {arrErrMsg ?? labels.empty}
                </p>
            ) : (
                fields.map((f, sIdx) => (
                    <SourceRow
                        key={f.id}
                        itemsFieldName={itemsFieldName}
                        idx={idx}
                        sIdx={sIdx}
                        options={options}
                        onSearchChange={onSearchChange}
                        clientFilter={clientFilter}
                        labels={labels}
                        onRemove={() => remove(sIdx)}
                    />
                ))
            )}
        </div>
    )
}
